<script lang="ts">
  const items = [
    {
      n: 'i.',
      h: 'The 6-step ritual',
      p: 'Open app, unlock, search, wait for refresh, copy, paste. Every login. Every time. There is a faster path.'
    },
    {
      n: 'ii.',
      h: 'Race-the-clock anxiety',
      p: 'Codes expire in 30 seconds. You see <5s left and your fingers fumble. The login times out. You start over.'
    },
    {
      n: 'iii.',
      h: 'Your secrets, their cloud',
      p: 'Authy, Microsoft, Google sync your TOTP seeds to their servers. They are promised not to peek. They could, mathematically.'
    },
    {
      n: 'iv.',
      h: 'Recovery is a trap',
      p: 'Lose your phone? Aegis: lost forever. Ente: account required. The honest answer is rarer than you would think.'
    }
  ];
</script>

<section id="problem" class="section scroll-reveal">
  <div class="wrap">
    <span class="eyebrow">The problem</span>
    <h2 class="h-section heading">
      Every authenticator app makes you <em>race the clock.</em>
    </h2>
    <p class="lede">
      Six steps, every time. Open. Unlock. Search. Wait. Copy. Paste. Half the time you miss the
      window. The other half, your secrets are syncing to a vendor you have never met.
    </p>

    <div class="problem-grid">
      {#each items as item (item.n)}
        <div class="problem-card">
          <div class="problem-num">{item.n}</div>
          <div class="problem-h">{item.h}</div>
          <div class="problem-p">{item.p}</div>
        </div>
      {/each}
    </div>
  </div>
</section>

<style>
  .section {
    padding: 96px 0;
    position: relative;
  }
  @media (max-width: 640px) {
    .section {
      padding: 64px 0;
    }
  }
  :global([data-theme='brutalist']) .section {
    border-bottom: 2px solid var(--text);
  }
  .wrap {
    max-width: var(--max-w);
    margin: 0 auto;
    padding: 0 var(--gutter);
  }
  @media (max-width: 640px) {
    .wrap {
      padding: 0 var(--gutter-sm);
    }
  }
  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
    padding: 6px 12px;
    border: 1px solid var(--border-2);
    border-radius: 999px;
    background: var(--bg-card);
  }
  :global([data-theme='brutalist']) .eyebrow {
    border-radius: 0;
    border: 2px solid var(--text);
    background: var(--accent-dim);
    color: var(--text);
    font-weight: 800;
  }
  .heading {
    margin-top: 18px;
    max-width: 780px;
  }
  .h-section {
    font-size: clamp(32px, 5vw, 56px);
    font-weight: 700;
    letter-spacing: -0.035em;
    line-height: 1;
  }
  .h-section em {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .h-section {
    font-weight: 900;
  }
  :global([data-theme='brutalist']) .h-section em {
    color: var(--text);
  }
  .lede {
    margin-top: 18px;
    font-size: clamp(16px, 1.7vw, 20px);
    color: var(--text-2);
    max-width: 640px;
    line-height: 1.55;
  }
  .problem-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-top: 48px;
  }
  @media (max-width: 960px) {
    .problem-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (max-width: 540px) {
    .problem-grid {
      grid-template-columns: 1fr;
    }
  }
  .problem-card {
    padding: 28px 24px;
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius-lg);
    position: relative;
    transition:
      background var(--t),
      border-color var(--t),
      transform var(--t);
  }
  .problem-card:hover {
    background: var(--bg-card-hover);
    border-color: var(--border-2);
    transform: translateY(-2px);
  }
  :global([data-theme='brutalist']) .problem-card {
    border: 2px solid var(--text);
    box-shadow: 4px 4px 0 var(--text);
  }
  :global([data-theme='brutalist']) .problem-card:hover {
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0 var(--text);
  }
  .problem-num {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: 14px;
    color: var(--accent);
    letter-spacing: 0.04em;
    margin-bottom: 12px;
  }
  :global([data-theme='brutalist']) .problem-num {
    color: var(--text);
    font-weight: 600;
  }
  .problem-h {
    font-size: 17px;
    font-weight: 700;
    line-height: 1.25;
    margin-bottom: 8px;
    letter-spacing: -0.01em;
  }
  :global([data-theme='brutalist']) .problem-h {
    font-weight: 800;
  }
  .problem-p {
    font-size: 13px;
    color: var(--text-3);
    line-height: 1.55;
  }
</style>
